import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DonkeyMascot } from "@/components/DonkeyMascot";
import { Button } from "@/components/ui/button";
import { VerdictBadge } from "@/components/DonkeyBits";
import { toast } from "sonner";
import {
  Plus, ArrowRight, Archive, Lock, Unlock, Briefcase, FileText, Loader2, CreditCard, Download,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [payments, setPayments] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [ent, setEnt] = useState(null);
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    try {
      const [r, w, p, bz, e, j] = await Promise.all([
        api.get("/reports"), api.get("/workspaces"), api.get("/payments/history"),
        api.get("/build/businesses").catch(() => ({ data: { businesses: [] } })),
        api.get("/me/entitlement").catch(() => ({ data: { entitlement: null } })),
        api.get("/me/journey").catch(() => ({ data: null })),
      ]);
      setReports(r.data.reports); setWorkspaces(w.data.workspaces); setPayments(p.data.payments);
      setBusinesses(bz.data.businesses); setEnt(e.data.entitlement); setJourney(j.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const archive = async (rid) => {
    await api.post(`/reports/${rid}/archive`);
    toast.success("Report archived.");
    load();
  };

  const visibleReports = reports.filter((r) => showArchived ? true : !r.archived);

  const FEATURES = [
    { key: "full_verdict_all", label: "Donkey's Verdict", plan: "prove", desc: "Full personalised verdict & three opportunities", to: "/interview" },
    { key: "validation_workspace", label: "Validation workspace", plan: "prove", desc: "Test assumptions with experiments & evidence", to: workspaces[0] ? `/workspace/${workspaces[0].id}` : "/interview" },
    { key: "brand", label: "Build a business", plan: "build", desc: "Positioning, brand, offer & pricing", to: businesses[0] ? `/build/${businesses[0].id}` : "/interview" },
    { key: "launch_all", label: "Launch", plan: "launch", desc: "Website & launch preparation", to: businesses[0] ? `/launch/${businesses[0].id}` : "/pricing" },
    { key: "crm", label: "Grow & scale", plan: "grow", desc: "Marketing, CRM & customer acquisition", to: businesses[0] ? `/grow/${businesses[0].id}` : "/pricing" },
  ];
  const entFeatures = ent?.features || [];
  const isAdmin = ent?.entitlement_source === "admin";
  const hasFeature = (k) => isAdmin || entFeatures.includes(k);
  const planName = (p) => ({ prove: "Prove", build: "Build", launch: "Launch", grow: "Grow", scale: "Scale" }[p] || p);

  if (loading) {
    return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-donkey-orange" /></div>;
  }

  return (
    <div className="mx-auto max-w-6xl px-5 sm:px-8 py-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-donkey-warm">
            {user?.name ? `Right then, ${user.name}.` : "Welcome back."}
          </h1>
          <p className="text-donkey-grey mt-2">The honest overview of your work so far.</p>
        </div>
        <Button onClick={() => navigate("/interview")} variant="outline"
          className="border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5 font-semibold h-11 px-6"
          data-testid="start-interview-btn">
          <Plus className="w-4 h-4 mr-2" /> Start new interview
        </Button>
      </div>

      {/* Continue your journey — one clear next step */}
      {journey && (
        <section className="mb-10 rounded-2xl border-2 border-donkey-orange/40 bg-gradient-to-br from-donkey-orange/10 to-transparent p-6 sm:p-8" data-testid="continue-journey">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-[0.2em] text-donkey-orange mb-2">Continue your journey</p>
              <h2 className="font-heading font-bold text-2xl text-donkey-warm" data-testid="journey-headline">{journey.primary.label}</h2>
              {journey.primary.note && <p className="text-sm text-donkey-grey mt-2 max-w-xl">{journey.primary.note}</p>}
              {journey.progress && journey.has_prove && (
                <div className="flex items-center gap-1.5 mt-4 flex-wrap" data-testid="journey-progress">
                  {journey.progress.map((s, i) => (
                    <div key={s.label} className="flex items-center gap-1.5">
                      <span className={`text-xs px-2.5 py-1 rounded-full border ${s.current ? "border-donkey-orange text-donkey-orange bg-donkey-orange/10" : s.done ? "border-donkey-green/40 text-donkey-green" : "border-white/15 text-donkey-grey"}`}>
                        {s.done ? "✓ " : ""}{s.label}
                      </span>
                      {i < journey.progress.length - 1 && <span className="text-donkey-grey text-xs">→</span>}
                    </div>
                  ))}
                </div>
              )}
              <p className="text-sm text-donkey-grey mt-3" data-testid="journey-plan">
                Your plan: <span className="text-donkey-warm font-semibold">{journey.entitlement_source === "admin" ? "Admin — full access" : `${journey.plan_name}${journey.has_prove ? " — Active" : ""}`}</span>
                {journey.has_prove && <span className="text-donkey-grey"> · Upgrading is optional — only needed for the next plan's features.</span>}
              </p>
            </div>
            <div className="shrink-0">
              {journey.primary.disabled ? (
                <Button disabled className="bg-donkey-orange/50 text-donkey-black font-bold rounded-full px-8 h-12 cursor-default" data-testid="journey-primary-disabled">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…
                </Button>
              ) : (
                <Button onClick={() => navigate(journey.primary.route)} className={`${journey.primary.upgrade ? "border border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5" : "bg-donkey-orange text-donkey-black hover:bg-donkey-deeporange shadow-[0_4px_20px_rgba(249,115,22,0.3)]"} font-bold rounded-full px-8 h-12 hover:-translate-y-0.5 transition-all`} data-testid="journey-primary-btn">
                  {journey.primary.label} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Plan & features overview */}
      {ent && (
        <section className="mb-12" data-testid="dashboard-plan-overview">
          <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
            <h2 className="font-heading font-semibold text-xl text-donkey-warm">Your plan & features</h2>
            <span className="text-sm px-3 py-1 rounded-full bg-donkey-orange/10 text-donkey-orange border border-donkey-orange/30" data-testid="dashboard-plan-badge">
              {isAdmin ? "Admin — full access" : `${ent.effective_plan_name}${ent.status === "active" && ent.renews_at ? "" : ""}`}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => {
              const unlocked = hasFeature(f.key);
              return (
                <div key={f.key} className={`card-donkey rounded-2xl p-5 flex flex-col ${unlocked ? "" : "opacity-90"}`} data-testid={`feature-${f.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-heading font-semibold text-donkey-warm">{f.label}</h3>
                    {unlocked
                      ? <span className="flex items-center gap-1 text-xs text-donkey-green"><Unlock className="w-3.5 h-3.5" /> Available</span>
                      : <span className="flex items-center gap-1 text-xs text-donkey-amber"><Lock className="w-3.5 h-3.5" /> {planName(f.plan)}</span>}
                  </div>
                  <p className="text-sm text-donkey-grey flex-1">{f.desc}</p>
                  <div className="mt-4">
                    {unlocked ? (
                      <Button size="sm" variant="outline" onClick={() => navigate(f.to)} className="border-donkey-line bg-transparent text-donkey-warm hover:bg-white/5" data-testid={`feature-open-${f.key}`}>
                        Open <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => navigate("/pricing")} className="border-white/15 bg-transparent text-donkey-warm hover:bg-white/5" data-testid={`feature-upgrade-${f.key}`}>
                        Requires {planName(f.plan)} — View plans
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Reports */}
      <section className="mb-12">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading font-semibold text-xl text-donkey-warm flex items-center gap-2">
            <FileText className="w-5 h-5 text-donkey-orange" /> Your reports
          </h2>
          {reports.some((r) => r.archived) && (
            <button onClick={() => setShowArchived((s) => !s)} className="text-xs text-donkey-grey hover:text-donkey-warm">
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          )}
        </div>

        {visibleReports.length === 0 ? (
          <div className="card-donkey rounded-2xl p-10 text-center">
            <DonkeyMascot size={120} className="mx-auto mb-5 opacity-90" />
            <p className="text-donkey-warm font-medium">No verdicts yet.</p>
            <p className="text-donkey-grey text-sm mt-1">The donkey cannot judge information it hasn't been given.</p>
            <Button onClick={() => navigate("/interview")} className="mt-6 bg-donkey-orange text-donkey-black font-semibold">
              Talk to the Donkey
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibleReports.map((r) => (
              <div key={r.id} className={`card-donkey rounded-2xl p-6 ${r.archived ? "opacity-60" : ""}`} data-testid={`report-${r.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-donkey-grey">{fmtDate(r.created_at)}</span>
                  {r.gen_status === "complete" ? (
                    r.unlocked
                      ? <span className="flex items-center gap-1 text-xs text-donkey-green"><Unlock className="w-3.5 h-3.5" /> Unlocked</span>
                      : <span className="flex items-center gap-1 text-xs text-donkey-amber"><Lock className="w-3.5 h-3.5" /> Snapshot</span>
                  ) : r.gen_status === "failed"
                    ? <span className="text-xs text-donkey-red">Failed</span>
                    : <span className="text-xs text-donkey-grey">Generating…</span>}
                </div>
                <h3 className="font-heading font-semibold text-donkey-warm line-clamp-1">{r.snapshot?.title || "Report"}</h3>
                {r.snapshot?.verdict_label && <div className="mt-3"><VerdictBadge label={r.snapshot.verdict_label} /></div>}
                <div className="mt-5 flex items-center justify-between">
                  <Button size="sm" variant="ghost" className="text-donkey-orange hover:bg-white/5 px-0"
                    onClick={() => navigate(r.gen_status === "complete" ? `/report/${r.id}` : `/analysis/${r.id}`)}
                    data-testid={`open-report-${r.id}`}>
                    Open <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                  <button onClick={() => archive(r.id)} className="text-donkey-grey hover:text-donkey-warm" aria-label="Archive">
                    <Archive className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Workspaces */}
      <section className="mb-12">
        <h2 className="font-heading font-semibold text-xl text-donkey-warm flex items-center gap-2 mb-5">
          <Briefcase className="w-5 h-5 text-donkey-orange" /> Business workspaces
        </h2>
        {workspaces.length === 0 ? (
          <div className="card-donkey rounded-2xl p-8 text-center">
            <p className="text-donkey-grey text-sm">No workspace yet. Unlock a verdict and select an opportunity to start validating it.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {workspaces.map((w) => (
              <button key={w.id} onClick={() => navigate(`/workspace/${w.id}`)}
                className="text-left card-donkey rounded-2xl p-6 hover:-translate-y-1 transition-transform" data-testid={`workspace-${w.id}`}>
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange mb-2">{w.status}</p>
                <h3 className="font-heading font-semibold text-donkey-warm">{w.name}</h3>
                <p className="text-xs text-donkey-grey mt-2">Updated {fmtDate(w.updated_at)}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Businesses (Stage 3 Build) */}
      <section className="mb-12">
        <h2 className="font-heading font-semibold text-xl text-donkey-warm flex items-center gap-2 mb-5">
          <Briefcase className="w-5 h-5 text-donkey-orange" /> Businesses (Build)
        </h2>
        {businesses.length === 0 ? (
          <div className="card-donkey rounded-2xl p-8 text-center">
            <p className="text-donkey-grey text-sm">No businesses yet. Once an opportunity passes Stage 2 validation, open it and choose “Build this opportunity”.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {businesses.map((bz) => (
              <button key={bz.id} onClick={() => navigate(`/build/${bz.id}`)}
                className="text-left card-donkey rounded-2xl p-6 hover:-translate-y-1 transition-transform" data-testid={`business-${bz.id}`}>
                <p className="text-xs uppercase tracking-[0.15em] text-donkey-orange mb-2">{bz.published ? "Published" : "In progress"}</p>
                <h3 className="font-heading font-semibold text-donkey-warm">{bz.name}</h3>
                <p className="text-xs text-donkey-grey mt-2">Updated {fmtDate(bz.updated_at)}</p>
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Billing */}
      <section>
        <h2 className="font-heading font-semibold text-xl text-donkey-warm flex items-center gap-2 mb-5">
          <CreditCard className="w-5 h-5 text-donkey-orange" /> Account & billing
        </h2>
        <div className="card-donkey rounded-2xl p-6">
          <p className="text-sm text-donkey-grey">Signed in as <span className="text-donkey-warm">{user?.email}</span></p>
          {payments.length > 0 ? (
            <div className="mt-4 space-y-2">
              {payments.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm border-t border-white/10 pt-2">
                  <span className="text-donkey-warm/80 capitalize">{(p.plan || "").replace(/_/g, " ")}</span>
                  <span className="font-mono text-donkey-grey">£{Number(p.amount).toFixed(2)}</span>
                  <span className={`text-xs ${p.payment_status === "paid" ? "text-donkey-green" : "text-donkey-grey"}`}>{p.payment_status}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-donkey-grey mt-2">No payments yet.</p>
          )}
          <div className="mt-5 flex gap-3 flex-wrap">
            <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-donkey-warm hover:bg-white/5"
              onClick={() => navigate("/account")} data-testid="plan-usage-btn">
              <CreditCard className="w-4 h-4 mr-1.5" /> Plan & usage
            </Button>
            <Button size="sm" variant="outline" className="border-white/15 bg-transparent text-donkey-warm hover:bg-white/5"
              onClick={async () => { const { data } = await api.get("/auth/export"); const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "honest-donkey-data.json"; a.click(); }}
              data-testid="export-data-btn">
              <Download className="w-4 h-4 mr-1.5" /> Export my data
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
